// Admin console (F-PLAT-6): a light-theme console app-shell — nav sidebar +
// white "island" content panel — matching the Armonika/Cloudz console look in
// our own CSS-token system (no Tailwind, no deps; scoped to .bz-console).
// Sections: Overview (stats + health), Live meetings (+ emergency end),
// admin-wide History, Audit log. Admin-gated server-side; metadata only (§11).

import {
  ChangeEvent,
  FocusEvent,
  FormEvent,
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router';

import {
  AdminMeetingHistoryEntry,
  api,
  ApiError,
  AuditEvent,
  LiveMeeting,
} from '../api';
import { AdminUser, OrgSettings } from '../api';
import { EmailSettingsUpdate, EmailSettingsView } from '../api';
import { LdapConfig, LdapSyncResult, MfaConfig, SsoProvider } from '../api';
import { RingtonePicker } from '../components/RingtonePicker';
import { AdminDashboard } from '../components/AdminDashboard';
import { IconClose, IconDots, IconPlus } from '../ui/icons';
import { DEFAULT_RINGTONE, KNOCK_SOUNDS, playKnockSound } from '../sounds';
import { Toggle } from '../ui/Toggle';
import { casdoorBase, loginUrl } from '../api';
import logoUrl from '../assets/logo-console.svg';

type Section =
  | 'dashboard'
  | 'live'
  | 'history'
  | 'audit'
  | 'users'
  | 'authentication'
  | 'settings';

const NAV: { key: Section; label: string; icon: ReactElement; sub: string }[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    sub: 'Usage, activity and system health',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    key: 'live',
    label: 'Live meetings',
    sub: 'Meetings in progress',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M23 7l-7 5 7 5V7z" />
        <rect x="1" y="5" width="15" height="14" rx="2" />
      </svg>
    ),
  },
  {
    key: 'history',
    label: 'History',
    sub: 'Past meetings',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3v5h5" />
        <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
  },
  {
    key: 'audit',
    label: 'Audit log',
    sub: 'Access-grant history',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    key: 'users',
    label: 'Users',
    sub: 'Directory and platform-admin roles',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    key: 'authentication',
    label: 'Authentication',
    sub: 'Enterprise sign-in: directory (LDAP/AD), SSO and MFA',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2l7 3v6c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V5l7-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    key: 'settings',
    label: 'Settings',
    sub: 'Workspace policy defaults',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
];

export function AdminPage() {
  const navigate = useNavigate();
  const [gate, setGate] = useState<'ok' | 'anon' | 'denied'>('ok');
  const [section, setSection] = useState<Section>('dashboard');
  // The dashboard links out to other admin pages ("See in details"); it takes a
  // plain string so a chart component never has to know this page's union.
  const goSection = useCallback((s: string) => setSection(s as Section), []);

  useEffect(() => {
    api.adminOverview().catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 401) setGate('anon');
      else if (e instanceof ApiError && e.status === 403) setGate('denied');
    });
  }, []);

  const onNav = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setSection(e.currentTarget.dataset.section as Section);
  }, []);
  const goHome = useCallback(() => navigate('/'), [navigate]);

  if (gate === 'anon') {
    return (
      <div className="bz-admin-gate">
        <img src={logoUrl} alt="Brizina" className="bz-admin-gate-logo" />
        <h1>Admin console</h1>
        <p>Sign in to continue.</p>
        <a className="bz-cbtn" href={loginUrl}>
          Sign in
        </a>
      </div>
    );
  }
  if (gate === 'denied') {
    return (
      <div className="bz-admin-gate">
        <img src={logoUrl} alt="Brizina" className="bz-admin-gate-logo" />
        <h1>Admin console</h1>
        <p className="bz-error-text">This account doesn&rsquo;t have admin access.</p>
      </div>
    );
  }

  const active = NAV.find((n) => n.key === section)!;

  return (
    <div className="bz-console">
      <aside className="bz-console-sidebar">
        <div className="bz-console-brand">
          <img src={logoUrl} alt="Brizina" />
        </div>
        <div className="bz-console-navlabel">Admin</div>
        {NAV.map((n) => (
          <button
            key={n.key}
            type="button"
            className={section === n.key ? 'bz-console-nav bz-console-nav-active' : 'bz-console-nav'}
            data-section={n.key}
            onClick={onNav}
          >
            {n.icon}
            {n.label}
          </button>
        ))}
        <div className="bz-console-sidebar-foot">
          <button type="button" className="bz-console-nav" onClick={goHome}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to app
          </button>
        </div>
      </aside>

      <main className="bz-console-panel">
        <header className="bz-console-header">
          <div>
            <div className="bz-console-title">{active.label}</div>
            <div className="bz-console-sub">{active.sub}</div>
          </div>
        </header>
        <div className="bz-console-content">
          {section === 'dashboard' && <AdminDashboard onNavigate={goSection} />}
          {section === 'live' && <LiveSection />}
          {section === 'history' && <HistorySection />}
          {section === 'audit' && <AuditSection />}
          {section === 'users' && <UsersSection />}
          {section === 'authentication' && <AuthenticationSection />}
          {section === 'settings' && <SettingsSection />}
        </div>
      </main>
    </div>
  );
}

// ── Users (Phase D directory + platform-admin role) ─────────────────────────

// Casdoor manages credentials/suspend/reset; the console deep-links there
// (casdoorBase lives in ../api, shared with the signup link).

type UserModalState =
  | { kind: 'create' }
  | { kind: 'edit'; user: AdminUser }
  | { kind: 'delete'; user: AdminUser }
  | { kind: 'reauth'; user: AdminUser }; // confirm-then-promote

function UsersSection() {
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [q, setQ] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [modal, setModal] = useState<UserModalState | null>(null);

  const load = useCallback((query: string) => {
    api
      .adminUsers(query)
      .then((r) => {
        setRows(r);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load('');
  }, [load]);

  const onSearch = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      load(q.trim());
    },
    [q, load],
  );
  const onQ = useCallback((e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value), []);

  const closeMenu = useCallback(() => setMenuFor(null), []);
  const toggleMenu = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const id = e.currentTarget.dataset.id!;
    setMenuFor((m) => (m === id ? null : id));
  }, []);
  const byId = useCallback((id: string) => rows.find((u) => u.id === id) ?? null, [rows]);

  // Make admin → re-auth modal; Revoke → immediate (no confirmation needed).
  const onRole = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      setMenuFor(null);
      const u = byId(e.currentTarget.dataset.id!);
      if (!u) return;
      if (u.isAdmin) {
        void api
          .adminSetUserRole(u.id, false)
          .then(() => setRows((rs) => rs.map((x) => (x.id === u.id ? { ...x, isAdmin: false } : x))));
      } else {
        setModal({ kind: 'reauth', user: u });
      }
    },
    [byId],
  );
  const onEdit = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      setMenuFor(null);
      const u = byId(e.currentTarget.dataset.id!);
      if (u) setModal({ kind: 'edit', user: u });
    },
    [byId],
  );
  const onDelete = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      setMenuFor(null);
      const u = byId(e.currentTarget.dataset.id!);
      if (u) setModal({ kind: 'delete', user: u });
    },
    [byId],
  );
  const openCreate = useCallback(() => setModal({ kind: 'create' }), []);
  const closeModal = useCallback(() => setModal(null), []);
  const onReloaded = useCallback(() => {
    setModal(null);
    load(q.trim());
  }, [q, load]);
  const onPromoted = useCallback((id: string) => {
    setRows((rs) => rs.map((x) => (x.id === id ? { ...x, isAdmin: true } : x)));
    setModal(null);
  }, []);

  return (
    <section>
      <div className="bz-cnote-row">
        <p className="bz-cnote">
          Directory mirrored from the identity provider. Suspend and reset passwords in Casdoor.
        </p>
        <div className="bz-crow-actions">
          <button type="button" className="bz-cbtn bz-cbtn-sm" onClick={openCreate}>
            <IconPlus size={14} /> Create user
          </button>
          <a className="bz-cbtn bz-cbtn-outline bz-cbtn-sm" href={`${casdoorBase()}/organizations/brizina/users`} target="_blank" rel="noreferrer">
            Manage in Casdoor ↗
          </a>
        </div>
      </div>
      <form className="bz-csearch" onSubmit={onSearch}>
        <input className="bz-cinput" placeholder="search name or email" value={q} onChange={onQ} aria-label="Search users" />
        <button className="bz-cbtn bz-cbtn-outline" type="submit">
          Search
        </button>
      </form>
      <div className="bz-ctable-wrap">
        <table className="bz-ctable">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Last seen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id}>
                <td>
                  <strong>{u.name}</strong>
                </td>
                <td>{u.email ?? '—'}</td>
                <td>
                  {u.isAdmin ? (
                    <span className="bz-cpill bz-cpill-primary">admin</span>
                  ) : (
                    <span className="bz-cpill bz-cpill-gray">member</span>
                  )}
                </td>
                <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                <td>{u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleDateString() : '—'}</td>
                <td>
                  <div className="bz-cdropdown">
                    <button type="button" className="bz-cicon-btn" data-id={u.id} onClick={toggleMenu} aria-label="Actions">
                      <IconDots size={16} />
                    </button>
                    {menuFor === u.id && (
                      <>
                        <div className="bz-cdropdown-backdrop" onClick={closeMenu} />
                        <div className="bz-cmenu">
                          <button type="button" className="bz-cmenu-item" data-id={u.id} onClick={onRole}>
                            {u.isAdmin ? 'Revoke admin' : 'Make admin'}
                          </button>
                          <button type="button" className="bz-cmenu-item" data-id={u.id} onClick={onEdit}>
                            Edit
                          </button>
                          <button type="button" className="bz-cmenu-item bz-cmenu-item-danger" data-id={u.id} onClick={onDelete}>
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {loaded && rows.length === 0 && (
              <tr>
                <td className="bz-ctable-empty" colSpan={6}>
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {modal && (
        <UserModal state={modal} onClose={closeModal} onReloaded={onReloaded} onPromoted={onPromoted} />
      )}
    </section>
  );
}

// Create / edit / delete a user, or confirm-then-promote (re-auth). One modal,
// four modes — reuses the console modal + field primitives.
function UserModal({
  state,
  onClose,
  onReloaded,
  onPromoted,
}: {
  state: UserModalState;
  onClose: () => void;
  onReloaded: () => void;
  onPromoted: (id: string) => void;
}) {
  const [name, setName] = useState(state.kind === 'edit' ? state.user.name : '');
  const [email, setEmail] = useState(state.kind === 'edit' ? (state.user.email ?? '') : '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const stop = useCallback((e: MouseEvent<HTMLDivElement>) => e.stopPropagation(), []);
  const onName = useCallback((e: ChangeEvent<HTMLInputElement>) => setName(e.target.value), []);
  const onEmail = useCallback((e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value), []);
  const onPassword = useCallback((e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value), []);

  const submit = useCallback(() => {
    setBusy(true);
    setErr(null);
    let req: Promise<unknown>;
    if (state.kind === 'create') {
      req = api.adminCreateUser(name, email, password);
    } else if (state.kind === 'edit') {
      const current = state.user.email ?? '';
      req = api.adminUpdateUser(state.user.id, current, name, email !== current ? email : undefined);
    } else if (state.kind === 'delete') {
      req = api.adminDeleteUser(state.user.id, state.user.email ?? '');
    } else {
      req = api.adminSetUserRole(state.user.id, true, password); // re-auth → promote
    }
    req
      .then(() => (state.kind === 'reauth' ? onPromoted(state.user.id) : onReloaded()))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Something went wrong'))
      .finally(() => setBusy(false));
  }, [state, name, email, password, onReloaded, onPromoted]);

  const title =
    state.kind === 'create'
      ? 'Create user'
      : state.kind === 'edit'
        ? 'Edit user'
        : state.kind === 'delete'
          ? 'Delete user'
          : 'Confirm it’s you';
  const cta =
    state.kind === 'create'
      ? 'Create'
      : state.kind === 'edit'
        ? 'Save'
        : state.kind === 'delete'
          ? 'Delete'
          : 'Grant admin';

  return (
    <div className="bz-cmodal-overlay" onClick={onClose}>
      <div className="bz-cmodal" onClick={stop} role="dialog" aria-label={title}>
        <div className="bz-cmodal-head">
          <span>{title}</span>
          <button type="button" className="bz-cmodal-x" onClick={onClose} aria-label="Close">
            <IconClose size={14} />
          </button>
        </div>
        <div className="bz-cmodal-body">
          {state.kind === 'delete' ? (
            <p className="bz-cfield-hint">
              Delete <strong>{state.user.name}</strong> ({state.user.email ?? 'no email'}) from the
              identity provider? They will no longer be able to sign in. This can’t be undone.
            </p>
          ) : state.kind === 'reauth' ? (
            <>
              <p className="bz-cfield-hint">
                Grant admin access to <strong>{state.user.name}</strong>. Confirm with your own
                password.
              </p>
              <div className="bz-cfield">
                <label>Your password</label>
                <input className="bz-cinput" type="password" value={password} onChange={onPassword} autoComplete="current-password" autoFocus />
              </div>
            </>
          ) : (
            <>
              <div className="bz-cfield">
                <label>Display name</label>
                <input className="bz-cinput" value={name} onChange={onName} placeholder="Jane Doe" autoFocus />
              </div>
              <div className="bz-cfield">
                <label>Email</label>
                <input className="bz-cinput" type="email" value={email} onChange={onEmail} placeholder="jane@example.com" />
              </div>
              {state.kind === 'create' && (
                <div className="bz-cfield">
                  <label>Temporary password</label>
                  <input className="bz-cinput" type="password" value={password} onChange={onPassword} autoComplete="new-password" />
                </div>
              )}
            </>
          )}
          {err && <p className="bz-cmodal-err">{err}</p>}
        </div>
        <div className="bz-cmodal-actions">
          <button type="button" className="bz-cbtn bz-cbtn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={state.kind === 'delete' ? 'bz-cbtn bz-cbtn-danger' : 'bz-cbtn'}
            onClick={submit}
            disabled={busy}
          >
            {busy ? 'Working…' : cta}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Settings (Phase C org policy defaults) ──────────────────────────────────

function SettingsSection() {
  const [tab, setTab] = useState<'general' | 'email' | 'audio'>('general');
  const onGeneral = useCallback(() => setTab('general'), []);
  const onEmail = useCallback(() => setTab('email'), []);
  const onAudio = useCallback(() => setTab('audio'), []);
  return (
    <>
      <div className="bz-cseg bz-csettings-tabs" role="tablist" aria-label="Settings">
        <button
          type="button"
          className={tab === 'general' ? 'bz-cseg-btn bz-cseg-btn-on' : 'bz-cseg-btn'}
          onClick={onGeneral}
        >
          General
        </button>
        <button
          type="button"
          className={tab === 'audio' ? 'bz-cseg-btn bz-cseg-btn-on' : 'bz-cseg-btn'}
          onClick={onAudio}
        >
          Audio
        </button>
        <button
          type="button"
          className={tab === 'email' ? 'bz-cseg-btn bz-cseg-btn-on' : 'bz-cseg-btn'}
          onClick={onEmail}
        >
          Email
        </button>
      </div>
      {tab === 'general' && <GeneralSettings />}
      {tab === 'audio' && <AudioSettings />}
      {tab === 'email' && <EmailSettings />}
    </>
  );
}

function AudioSettings() {
  const [st, setSt] = useState<OrgSettings | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.adminSettings().then(setSt).catch(() => setSt(null));
  }, []);

  const onStereo = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, audioStereo: !s.audioStereo } : s));
  }, []);
  const onEcho = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, audioEchoCancellation: !s.audioEchoCancellation } : s));
  }, []);
  const onNoise = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, audioNoiseSuppression: !s.audioNoiseSuppression } : s));
  }, []);
  const onGain = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, audioAutoGain: !s.audioAutoGain } : s));
  }, []);
  const onCalls = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, callsEnabled: !s.callsEnabled } : s));
  }, []);
  const onRingtone = useCallback((id: string) => {
    setSaved(false);
    setSt((s) => (s ? { ...s, callRingtone: id } : s));
  }, []);

  const save = useCallback(() => {
    if (!st) return;
    setSaving(true);
    api
      .adminSaveSettings(st)
      .then(() => setSaved(true))
      .catch(() => setSaved(false))
      .finally(() => setSaving(false));
  }, [st]);

  if (!st) return null;
  const stereo = st.audioStereo;

  return (
    <section className="bz-csettings">
      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Calls</div>
          <div className="bz-cfield-hint">
            Let people call each other directly, one to one or as a group. Turning this off hides
            Calls everywhere and refuses new ones — conversations already connected finish
            normally.
          </div>
        </div>
        <Toggle on={st.callsEnabled} onToggle={onCalls} label="Calls" />
      </div>

      <h4 className="bz-csettings-head">Meeting audio</h4>
      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Stereo (high-fidelity)</div>
          <div className="bz-cfield-hint">
            Publishes 2-channel audio at ~96 kbps (music preset) instead of mono — better for
            music and instruments. Advise participants to wear headphones.
          </div>
        </div>
        <Toggle on={st.audioStereo} onToggle={onStereo} label="Stereo audio" />
      </div>

      <div className="bz-cfield">
        <label>Incoming-call ringtone</label>
        <RingtonePicker value={st.callRingtone || DEFAULT_RINGTONE} onChange={onRingtone} />
        <p className="bz-cfield-hint">
          What everyone in the workspace hears when someone calls them. Picking one plays it; the
          button replays or stops it.
        </p>
      </div>

      <h4 className="bz-csettings-head">Processing</h4>
      {stereo && (
        <p className="bz-cnote">
          Stereo mode bypasses the options below — browsers run echo/noise/gain in mono, so they
          can’t apply alongside stereo. Turn stereo off to use them.
        </p>
      )}
      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Echo cancellation</div>
          <div className="bz-cfield-hint">Removes echo/feedback. Recommended for meetings.</div>
        </div>
        <Toggle
          on={st.audioEchoCancellation && !stereo}
          onToggle={onEcho}
          label="Echo cancellation"
          disabled={stereo}
        />
      </div>
      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Noise suppression</div>
          <div className="bz-cfield-hint">Suppresses steady background noise (fans, keyboards).</div>
        </div>
        <Toggle
          on={st.audioNoiseSuppression && !stereo}
          onToggle={onNoise}
          label="Noise suppression"
          disabled={stereo}
        />
      </div>
      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Auto gain control</div>
          <div className="bz-cfield-hint">Evens out mic levels across speakers.</div>
        </div>
        <Toggle
          on={st.audioAutoGain && !stereo}
          onToggle={onGain}
          label="Auto gain control"
          disabled={stereo}
        />
      </div>

      <div className="bz-csettings-actions">
        <button className="bz-cbtn" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span className="bz-csaved">Saved ✓</span>}
      </div>
    </section>
  );
}

function GeneralSettings() {
  const [st, setSt] = useState<OrgSettings | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.adminSettings().then(setSt).catch(() => setSt(null));
  }, []);

  const patch = useCallback((p: Partial<OrgSettings>) => {
    setSaved(false);
    setSt((s) => (s ? { ...s, ...p } : s));
  }, []);
  const onE2ee = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => patch({ defaultE2eeModel: e.target.value as 'a' | 'b' }),
    [patch],
  );
  const onGuests = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, allowGuests: !s.allowGuests } : s));
  }, []);
  const onRecording = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, allowRecording: !s.allowRecording } : s));
  }, []);
  const onFileSharing = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, allowFileSharing: !s.allowFileSharing } : s));
  }, []);
  const onPolls = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, allowPolls: !s.allowPolls } : s));
  }, []);
  const onTimer = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, allowTimer: !s.allowTimer } : s));
  }, []);
  const onNotes = useCallback(() => {
    setSaved(false);
    setSt((s) => (s ? { ...s, allowNotes: !s.allowNotes } : s));
  }, []);
  const onMaxAtt = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => patch({ maxAttendees: Number(e.target.value) }),
    [patch],
  );
  const onMaxDur = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => patch({ maxDurationMin: Number(e.target.value) }),
    [patch],
  );
  const onKnockSound = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      patch({ knockSound: e.target.value });
      playKnockSound(e.target.value); // preview on change
    },
    [patch],
  );

  const save = useCallback(() => {
    if (!st) return;
    setSaving(true);
    api
      .adminSaveSettings(st)
      .then(() => setSaved(true))
      .catch(() => setSaved(false))
      .finally(() => setSaving(false));
  }, [st]);

  if (!st) return null;

  return (
    <section className="bz-csettings">
      <div className="bz-cfield">
        <label>Default encryption model</label>
        <select
          className="bz-cinput"
          value={st.defaultE2eeModel}
          onChange={onE2ee}
        >
          <option value="a">Model A — server-managed key (default)</option>
          <option value="b">Model B — zero-knowledge (passphrase)</option>
        </select>
        <p className="bz-cfield-hint">Meetings are always end-to-end encrypted; this picks which model new meetings default to.</p>
      </div>

      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Allow guest access</div>
          <div className="bz-cfield-hint">Anonymous guests can knock to join with just a name.</div>
        </div>
        <Toggle on={st.allowGuests} onToggle={onGuests} label="Allow guests" />
      </div>

      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Allow recording</div>
          <div className="bz-cfield-hint">Hosts may record meetings (client-side; a recording indicator always shows).</div>
        </div>
        <Toggle on={st.allowRecording} onToggle={onRecording} label="Allow recording" />
      </div>

      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Allow file sharing</div>
          <div className="bz-cfield-hint">Attendees can share encrypted files in a meeting (F-COM-5). Files are end-to-end encrypted; only ciphertext is stored.</div>
        </div>
        <Toggle on={st.allowFileSharing} onToggle={onFileSharing} label="Allow file sharing" />
      </div>

      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Allow polls</div>
          <div className="bz-cfield-hint">Hosts can run polls from Meeting Tools; attendees vote. Questions and votes are end-to-end encrypted.</div>
        </div>
        <Toggle on={st.allowPolls} onToggle={onPolls} label="Allow polls" />
      </div>

      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Allow timer</div>
          <div className="bz-cfield-hint">Hosts can show a shared countdown timer from Meeting Tools.</div>
        </div>
        <Toggle on={st.allowTimer} onToggle={onTimer} label="Allow timer" />
      </div>

      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Allow notes</div>
          <div className="bz-cfield-hint">Collaborative shared notes in Meeting Tools. Content, cursors and presence are end-to-end encrypted; nothing is stored server-side.</div>
        </div>
        <Toggle on={st.allowNotes} onToggle={onNotes} label="Allow notes" />
      </div>

      <div className="bz-cfield">
        <label>Max attendees per meeting</label>
        <input
          className="bz-cinput bz-cinput-num"
          type="number"
          min={2}
          max={500}
          value={st.maxAttendees}
          onChange={onMaxAtt}
        />
      </div>

      <div className="bz-cfield">
        <label>Max meeting duration (minutes, 0 = unlimited)</label>
        <input
          className="bz-cinput bz-cinput-num"
          type="number"
          min={0}
          value={st.maxDurationMin}
          onChange={onMaxDur}
        />
      </div>

      <div className="bz-cfield">
        <label>Join-request sound</label>
        <select className="bz-cinput" value={st.knockSound || 'ding'} onChange={onKnockSound}>
          {KNOCK_SOUNDS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <p className="bz-cfield-hint">
          The chime a host hears when someone requests to join — the same for everyone in the
          workspace. Changing it here plays a preview.
        </p>
      </div>

      <div className="bz-csettings-actions">
        <button className="bz-cbtn" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span className="bz-csaved">Saved ✓</span>}
      </div>
    </section>
  );
}

function EmailSettings() {
  const [v, setV] = useState<EmailSettingsView | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [resendKey, setResendKey] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(() => {
    api
      .adminEmail()
      .then((r) => {
        setV(r.settings);
        setEnabled(r.enabled);
      })
      .catch(() => setV(null));
  }, []);
  useEffect(load, [load]);

  const onField = useCallback((e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const t = e.target;
    const key = t.name as keyof EmailSettingsView;
    const val: string | number = t.type === 'number' ? Number(t.value) : t.value;
    setSaved(false);
    setV((s) => (s ? ({ ...s, [key]: val } as EmailSettingsView) : s));
  }, []);
  const onProvider = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const p = e.currentTarget.dataset.provider as 'resend' | 'smtp';
    setSaved(false);
    setV((s) => (s ? { ...s, provider: p } : s));
  }, []);
  const onWelcome = useCallback(() => {
    setSaved(false);
    setV((s) => (s ? { ...s, welcomeEnabled: !s.welcomeEnabled } : s));
  }, []);
  const onInvite = useCallback(() => {
    setSaved(false);
    setV((s) => (s ? { ...s, inviteEnabled: !s.inviteEnabled } : s));
  }, []);
  const onResendKey = useCallback((e: ChangeEvent<HTMLInputElement>) => setResendKey(e.target.value), []);
  const onSmtpPassword = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSmtpPassword(e.target.value),
    [],
  );
  const onTestTo = useCallback((e: ChangeEvent<HTMLInputElement>) => setTestTo(e.target.value), []);

  const save = useCallback(() => {
    if (!v) return;
    setSaving(true);
    const payload: EmailSettingsUpdate = {
      provider: v.provider,
      fromName: v.fromName,
      fromEmail: v.fromEmail,
      replyTo: v.replyTo,
      smtpHost: v.smtpHost,
      smtpPort: v.smtpPort,
      smtpUsername: v.smtpUsername,
      smtpSecurity: v.smtpSecurity,
      welcomeEnabled: v.welcomeEnabled,
      inviteEnabled: v.inviteEnabled,
      inviteCap: v.inviteCap,
      rateWindowSec: v.rateWindowSec,
      resendApiKey: resendKey.trim() || undefined,
      smtpPassword: smtpPassword.trim() || undefined,
    };
    api
      .adminSaveEmail(payload)
      .then(() => {
        setSaved(true);
        setResendKey('');
        setSmtpPassword('');
        load();
      })
      .catch(() => setSaved(false))
      .finally(() => setSaving(false));
  }, [v, resendKey, smtpPassword, load]);

  const sendTest = useCallback(() => {
    setTesting(true);
    setTestMsg(null);
    api
      .adminTestEmail(testTo.trim() || undefined)
      .then((r) => setTestMsg({ ok: true, text: `Test email sent to ${r.to}.` }))
      .catch((e: unknown) =>
        setTestMsg({ ok: false, text: e instanceof Error ? e.message : 'Send failed' }),
      )
      .finally(() => setTesting(false));
  }, [testTo]);

  if (!v) return null;

  return (
    <section className="bz-csettings">
      <div className="bz-cemail-status">
        <span className={enabled ? 'bz-cstatus bz-cstatus-on' : 'bz-cstatus bz-cstatus-off'}>
          <span className="bz-cstatus-dot" />
          {enabled ? 'Sending enabled' : 'Not configured'}
        </span>
      </div>

      <div className="bz-cfield">
        <label>Provider</label>
        <div className="bz-cseg" role="tablist" aria-label="Email provider">
          <button
            type="button"
            data-provider="resend"
            className={v.provider === 'resend' ? 'bz-cseg-btn bz-cseg-btn-on' : 'bz-cseg-btn'}
            onClick={onProvider}
          >
            API key (Resend)
          </button>
          <button
            type="button"
            data-provider="smtp"
            className={v.provider === 'smtp' ? 'bz-cseg-btn bz-cseg-btn-on' : 'bz-cseg-btn'}
            onClick={onProvider}
          >
            SMTP
          </button>
        </div>
      </div>

      <div className="bz-cgrid2">
        <div className="bz-cfield">
          <label>From name</label>
          <input className="bz-cinput" name="fromName" value={v.fromName} onChange={onField} placeholder="Brizina" />
        </div>
        <div className="bz-cfield">
          <label>From address</label>
          <input className="bz-cinput" name="fromEmail" type="email" value={v.fromEmail} onChange={onField} placeholder="noreply@yourdomain.com" />
        </div>
      </div>
      <div className="bz-cfield">
        <label>Reply-to (optional)</label>
        <input className="bz-cinput" name="replyTo" type="email" value={v.replyTo} onChange={onField} placeholder="hello@yourdomain.com" />
      </div>

      {v.provider === 'resend' ? (
        <div className="bz-cfield">
          <label>Resend API key</label>
          <input
            className="bz-cinput"
            type="password"
            value={resendKey}
            onChange={onResendKey}
            autoComplete="off"
            placeholder={v.hasResendKey ? `•••• ${v.resendKeyHint} (configured)` : 're_…'}
          />
          <p className="bz-cfield-hint">
            Leave blank to keep the current key. Stored server-side and never shown again. Your
            From address must be on a Resend-verified domain.
          </p>
        </div>
      ) : (
        <>
          <div className="bz-cgrid2">
            <div className="bz-cfield">
              <label>SMTP host</label>
              <input className="bz-cinput" name="smtpHost" value={v.smtpHost} onChange={onField} placeholder="smtp.yourprovider.com" />
            </div>
            <div className="bz-cfield">
              <label>Port</label>
              <input className="bz-cinput bz-cinput-num" name="smtpPort" type="number" min={1} max={65535} value={v.smtpPort} onChange={onField} />
            </div>
          </div>
          <div className="bz-cgrid2">
            <div className="bz-cfield">
              <label>Username</label>
              <input className="bz-cinput" name="smtpUsername" value={v.smtpUsername} onChange={onField} autoComplete="off" placeholder="apikey or user@domain" />
            </div>
            <div className="bz-cfield">
              <label>Password</label>
              <input
                className="bz-cinput"
                type="password"
                value={smtpPassword}
                onChange={onSmtpPassword}
                autoComplete="off"
                placeholder={v.hasSmtpPassword ? '•••• (configured)' : ''}
              />
            </div>
          </div>
          <div className="bz-cfield">
            <label>Encryption</label>
            <select className="bz-cinput" name="smtpSecurity" value={v.smtpSecurity} onChange={onField}>
              <option value="starttls">STARTTLS (usually port 587)</option>
              <option value="tls">SSL/TLS (usually port 465)</option>
              <option value="none">None — unencrypted</option>
            </select>
            <p className="bz-cfield-hint">Password is stored server-side; leave blank to keep the current one.</p>
          </div>
        </>
      )}

      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Welcome email</div>
          <div className="bz-cfield-hint">Sent to new users after they register.</div>
        </div>
        <Toggle on={v.welcomeEnabled} onToggle={onWelcome} label="Welcome email" />
      </div>
      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Meeting invites</div>
          <div className="bz-cfield-hint">Hosts can invite attendees by email when scheduling.</div>
        </div>
        <Toggle on={v.inviteEnabled} onToggle={onInvite} label="Meeting invites" />
      </div>

      <div className="bz-cgrid2">
        <div className="bz-cfield">
          <label>Invite recipient cap</label>
          <input className="bz-cinput bz-cinput-num" name="inviteCap" type="number" min={1} max={100} value={v.inviteCap} onChange={onField} />
          <p className="bz-cfield-hint">Max recipients per invite (1–100).</p>
        </div>
        <div className="bz-cfield">
          <label>Invite rate window (seconds)</label>
          <input className="bz-cinput bz-cinput-num" name="rateWindowSec" type="number" min={10} max={3600} value={v.rateWindowSec} onChange={onField} />
          <p className="bz-cfield-hint">Throttle window for repeated sends (10–3600).</p>
        </div>
      </div>

      <div className="bz-cfield">
        <label>Send a test email</label>
        <div className="bz-ctest">
          <input className="bz-cinput" type="email" value={testTo} onChange={onTestTo} placeholder="you@example.com (defaults to your address)" />
          <button type="button" className="bz-cbtn bz-cbtn-outline" onClick={sendTest} disabled={testing}>
            {testing ? 'Sending…' : 'Send test'}
          </button>
        </div>
        <p className="bz-cfield-hint">Uses the currently saved config — save first if you just changed something.</p>
        {testMsg && <p className={testMsg.ok ? 'bz-ctest-ok' : 'bz-ctest-err'}>{testMsg.text}</p>}
      </div>

      <div className="bz-csettings-actions">
        <button className="bz-cbtn" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span className="bz-csaved">Saved ✓</span>}
      </div>
    </section>
  );
}

// ── Authentication (enterprise sign-in: SSO + directory) ────────────────────
const EMPTY_LDAP: LdapConfig = {
  id: '',
  serverName: '',
  host: '',
  port: 389,
  enableSsl: false,
  username: '',
  password: '',
  baseDn: '',
  filter: '(objectClass=inetOrgPerson)',
  autoSync: 0,
};

// SSO presets — friendly label + Casdoor type + which extra field each needs.
const SSO_TYPES: { type: string; label: string; extra: 'none' | 'domain' | 'issuer'; domainLabel?: string }[] = [
  { type: 'Google', label: 'Google Workspace', extra: 'none' },
  { type: 'AzureAD', label: 'Microsoft Entra ID', extra: 'domain', domainLabel: 'Tenant ID' },
  { type: 'Okta', label: 'Okta', extra: 'domain', domainLabel: 'Okta domain (company.okta.com)' },
  { type: 'OpenID Connect', label: 'Generic OIDC / OAuth2', extra: 'issuer' },
];

const EMPTY_SSO: SsoProvider = {
  name: '',
  displayName: '',
  type: 'Google',
  clientId: '',
  clientSecret: '',
  scopes: 'openid profile email',
  domain: '',
  issuerUrl: '',
  customAuthUrl: '',
  customTokenUrl: '',
  customUserInfoUrl: '',
  enabled: true,
};

// "Add authentication" dropdown options.
const ADD_OPTIONS: { kind: 'sso' | 'ldap'; type: string; label: string }[] = [
  { kind: 'sso', type: 'Google', label: 'Google Workspace' },
  { kind: 'sso', type: 'AzureAD', label: 'Microsoft Entra ID' },
  { kind: 'sso', type: 'Okta', label: 'Okta' },
  { kind: 'sso', type: 'OpenID Connect', label: 'Generic OIDC / OAuth2' },
  { kind: 'ldap', type: '', label: 'LDAP / Active Directory' },
];

const ssoLabel = (t: string) => SSO_TYPES.find((x) => x.type === t)?.label ?? t;

type AuthModalState = { kind: 'sso'; data: SsoProvider } | { kind: 'ldap'; data: LdapConfig };

// Authentication tab: all enterprise sign-in methods (SSO + directory) in one
// data table; "Add authentication" opens a type dropdown → config modal. The
// backend proxies Casdoor. These are additive to password/email login, so
// nothing configured here can lock anyone out.
function AuthenticationSection() {
  const [providers, setProviders] = useState<SsoProvider[]>([]);
  const [ldaps, setLdaps] = useState<LdapConfig[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [modal, setModal] = useState<AuthModalState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    void Promise.allSettled([api.adminProviders(), api.adminLdaps()]).then(([p, l]) => {
      if (p.status === 'fulfilled') setProviders(p.value);
      if (l.status === 'fulfilled') setLdaps(l.value);
      const rej = ([p, l].find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined)?.reason;
      if (rej instanceof ApiError && rej.status === 503) setUnavailable(true);
      setLoaded(true);
    });
  }, []);
  useEffect(load, [load]);

  const toggleAdd = useCallback(() => setAddOpen((v) => !v), []);
  const closeAdd = useCallback(() => setAddOpen(false), []);
  const closeModal = useCallback(() => setModal(null), []);
  const onSaved = useCallback(
    (m: string) => {
      setModal(null);
      setMsg(m);
      load();
    },
    [load],
  );

  const onPick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const o = ADD_OPTIONS[Number(e.currentTarget.dataset.idx)]!;
    setAddOpen(false);
    setMsg(null);
    setModal(
      o.kind === 'sso'
        ? { kind: 'sso', data: { ...EMPTY_SSO, type: o.type } }
        : { kind: 'ldap', data: { ...EMPTY_LDAP } },
    );
  }, []);

  const onToggleSso = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const p = providers.find((x) => x.name === e.currentTarget.dataset.name);
      if (!p) return;
      setBusy('r:' + p.name);
      api
        .adminSaveProvider({ ...p, clientSecret: '', enabled: !p.enabled })
        .then(() => load())
        .catch((err) => setMsg(err instanceof Error ? err.message : 'Update failed'))
        .finally(() => setBusy(null));
    },
    [providers, load],
  );
  const onEditSso = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const p = providers.find((x) => x.name === e.currentTarget.dataset.name);
      if (p) setModal({ kind: 'sso', data: { ...p, clientSecret: '' } });
    },
    [providers],
  );
  const onEditLdap = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const l = ldaps.find((x) => x.id === e.currentTarget.dataset.id);
      if (l) setModal({ kind: 'ldap', data: { ...l, password: '' } });
    },
    [ldaps],
  );
  const onTestLdap = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const id = e.currentTarget.dataset.id!;
    setBusy('r:' + id);
    setMsg(null);
    api
      .adminTestLdap(id)
      .then((r) =>
        setMsg(
          `Connection OK — ${r.count} user${r.count === 1 ? '' : 's'} visible` +
            (r.count ? ': ' + r.users.slice(0, 8).map((u) => u.uid || u.cn).join(', ') : '') +
            (r.count > 8 ? '…' : ''),
        ),
      )
      .catch((err) => setMsg(err instanceof Error ? err.message : 'Connection test failed'))
      .finally(() => setBusy(null));
  }, []);
  const onImportLdap = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const id = e.currentTarget.dataset.id!;
    setBusy('r:' + id);
    setMsg(null);
    api
      .adminSyncLdap(id)
      .then((r: LdapSyncResult) => {
        const ex = r.existed?.length ?? 0;
        setMsg(
          `Imported ${r.imported} user${r.imported === 1 ? '' : 's'}` +
            (ex > 0 ? `, ${ex} already existed.` : '.'),
        );
      })
      .catch((err) => setMsg(err instanceof Error ? err.message : 'Import failed'))
      .finally(() => setBusy(null));
  }, []);
  const onRemove = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const { kind, id } = e.currentTarget.dataset as { kind: 'sso' | 'ldap'; id: string };
      const q =
        kind === 'sso'
          ? 'Remove this sign-in provider? Users who signed in with it will need another method.'
          : 'Remove this directory connection? Imported users keep their accounts.';
      if (!window.confirm(q)) return;
      setBusy('d:' + id);
      const req = kind === 'sso' ? api.adminDeleteProvider(id) : api.adminDeleteLdap(id);
      req
        .then(() => {
          setMsg('Removed.');
          load();
        })
        .catch((err) => setMsg(err instanceof Error ? err.message : 'Delete failed'))
        .finally(() => setBusy(null));
    },
    [load],
  );

  const total = providers.length + ldaps.length;

  return (
    <section>
      <div className="bz-cnote-row">
        <p className="bz-cnote">
          Enterprise sign-in methods. SSO and directory login are added alongside password and email
          login, so configuring them never locks anyone out.
        </p>
        <div className="bz-cdropdown">
          <button className="bz-cbtn" onClick={toggleAdd} disabled={unavailable}>
            Add authentication ▾
          </button>
          {addOpen && (
            <>
              <div className="bz-cdropdown-backdrop" onClick={closeAdd} />
              <div className="bz-cmenu" role="menu">
                {ADD_OPTIONS.map((o, i) => (
                  <button key={o.label} type="button" className="bz-cmenu-item" data-idx={i} onClick={onPick}>
                    {o.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {unavailable && (
        <p className="bz-cnote">Authentication management isn’t available in this environment.</p>
      )}
      {msg && <p className="bz-cnote">{msg}</p>}

      <div className="bz-ctable-wrap">
        <table className="bz-ctable">
          <thead>
            <tr>
              <th>Method</th>
              <th>Type</th>
              <th>Status</th>
              <th>Detail</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => (
              <tr key={'sso:' + p.name}>
                <td>
                  <strong>{p.displayName || ssoLabel(p.type)}</strong>
                </td>
                <td>Single Sign-On</td>
                <td>
                  {p.enabled ? (
                    <span className="bz-cpill bz-cpill-ok">shown</span>
                  ) : (
                    <span className="bz-cpill bz-cpill-gray">hidden</span>
                  )}
                </td>
                <td>
                  <code>{p.clientId}</code>
                </td>
                <td className="bz-ctable-actions">
                  <button
                    className="bz-cbtn bz-cbtn-sm bz-cbtn-outline"
                    disabled={busy === 'r:' + p.name}
                    data-name={p.name}
                    onClick={onToggleSso}
                  >
                    {p.enabled ? 'Hide' : 'Show'}
                  </button>
                  <button className="bz-cbtn bz-cbtn-sm bz-cbtn-outline" data-name={p.name} onClick={onEditSso}>
                    Edit
                  </button>
                  <button
                    className="bz-cbtn bz-cbtn-sm bz-cbtn-danger"
                    disabled={busy === 'd:' + p.name}
                    data-kind="sso"
                    data-id={p.name}
                    onClick={onRemove}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {ldaps.map((l) => (
              <tr key={'ldap:' + l.id}>
                <td>
                  <strong>{l.serverName || l.host}</strong>
                </td>
                <td>Directory (LDAP/AD)</td>
                <td>
                  <span className="bz-cpill bz-cpill-ok">connected</span>
                </td>
                <td>
                  <code>
                    {l.enableSsl ? 'ldaps://' : 'ldap://'}
                    {l.host}:{l.port}
                  </code>
                </td>
                <td className="bz-ctable-actions">
                  <button
                    className="bz-cbtn bz-cbtn-sm bz-cbtn-outline"
                    disabled={busy === 'r:' + l.id}
                    data-id={l.id}
                    onClick={onTestLdap}
                  >
                    Test
                  </button>
                  <button className="bz-cbtn bz-cbtn-sm" disabled={busy === 'r:' + l.id} data-id={l.id} onClick={onImportLdap}>
                    Import
                  </button>
                  <button className="bz-cbtn bz-cbtn-sm bz-cbtn-outline" data-id={l.id} onClick={onEditLdap}>
                    Edit
                  </button>
                  <button
                    className="bz-cbtn bz-cbtn-sm bz-cbtn-danger"
                    disabled={busy === 'd:' + l.id}
                    data-kind="ldap"
                    data-id={l.id}
                    onClick={onRemove}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {loaded && total === 0 && (
              <tr>
                <td className="bz-ctable-empty" colSpan={5}>
                  No enterprise sign-in configured. Password and email sign-in remain available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <MfaPanel />

      {modal && <AuthModal state={modal} onClose={closeModal} onSaved={onSaved} />}
    </section>
  );
}

// Two-step verification (opt-in): offer factors org-wide; users enrol from their
// own account. Casdoor enforces MFA only at the org level, so this is a single
// org policy (not per-app).
function MfaPanel() {
  const [cfg, setCfg] = useState<MfaConfig | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    api
      .adminMfa()
      .then((c) => setCfg(c))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 503) setUnavailable(true);
        setCfg({ enabled: false, factors: [] });
      });
  }, []);

  const toggleEnabled = useCallback(() => {
    setSaved(false);
    setCfg((c) =>
      c
        ? {
            ...c,
            enabled: !c.enabled,
            factors: !c.enabled && c.factors.length === 0 ? ['App'] : c.factors,
          }
        : c,
    );
  }, []);
  const toggleApp = useCallback(() => {
    setSaved(false);
    setCfg((c) =>
      c ? { ...c, factors: c.factors.includes('App') ? c.factors.filter((f) => f !== 'App') : [...c.factors, 'App'] } : c,
    );
  }, []);
  const toggleEmail = useCallback(() => {
    setSaved(false);
    setCfg((c) =>
      c
        ? { ...c, factors: c.factors.includes('Email') ? c.factors.filter((f) => f !== 'Email') : [...c.factors, 'Email'] }
        : c,
    );
  }, []);
  const save = useCallback(() => {
    if (!cfg) return;
    setSaving(true);
    api
      .adminSaveMfa({ enabled: cfg.enabled && cfg.factors.length > 0, factors: cfg.enabled ? cfg.factors : [] })
      .then(() => setSaved(true))
      .catch(() => setSaved(false))
      .finally(() => setSaving(false));
  }, [cfg]);

  if (unavailable || !cfg) return null;

  return (
    <section className="bz-csettings bz-cauth-mfa">
      <div className="bz-cfield-title">Two-step verification (2FA)</div>
      <div className="bz-cfield-hint">
        Offer 2-step verification to your workspace. It’s opt-in — users turn it on from their own
        account; nothing here forces it.
      </div>

      <div className="bz-cfield-row">
        <div>
          <div className="bz-cfield-title">Allow two-step verification</div>
          <div className="bz-cfield-hint">Lets users add a second factor to their sign-in.</div>
        </div>
        <Toggle on={cfg.enabled} onToggle={toggleEnabled} label="Allow 2FA" />
      </div>

      {cfg.enabled && (
        <>
          <div className="bz-cfield-row">
            <div>
              <div className="bz-cfield-title">Authenticator app</div>
              <div className="bz-cfield-hint">Time-based codes (Google Authenticator, 1Password, etc.).</div>
            </div>
            <Toggle on={cfg.factors.includes('App')} onToggle={toggleApp} label="Authenticator app" />
          </div>
          <div className="bz-cfield-row">
            <div>
              <div className="bz-cfield-title">Email code</div>
              <div className="bz-cfield-hint">A one-time code sent to the user’s email.</div>
            </div>
            <Toggle on={cfg.factors.includes('Email')} onToggle={toggleEmail} label="Email code" />
          </div>
          {cfg.factors.length === 0 && (
            <p className="bz-cnote">Pick at least one method, or turn off two-step verification.</p>
          )}
        </>
      )}

      <div className="bz-csettings-actions">
        <button className="bz-cbtn" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span className="bz-csaved">Saved ✓</span>}
      </div>
    </section>
  );
}

// Config modal for one auth method (SSO provider or LDAP directory).
function AuthModal({
  state,
  onClose,
  onSaved,
}: {
  state: AuthModalState;
  onClose: () => void;
  onSaved: (m: string) => void;
}) {
  const [sso, setSso] = useState<SsoProvider>(state.kind === 'sso' ? state.data : EMPTY_SSO);
  const [ldap, setLdap] = useState<LdapConfig>(state.kind === 'ldap' ? state.data : EMPTY_LDAP);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const preset = SSO_TYPES.find((t) => t.type === sso.type) ?? SSO_TYPES[0]!;
  const callbackUrl = `${casdoorBase()}/callback`;

  const onSsoField = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const k = e.currentTarget.dataset.field as keyof SsoProvider;
    const v = e.currentTarget.value;
    setSso((s) => ({ ...s, [k]: v }) as SsoProvider);
  }, []);
  const onLdapField = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const k = e.currentTarget.dataset.field as keyof LdapConfig;
    const raw = e.currentTarget.value;
    setLdap((s) => ({ ...s, [k]: k === 'port' ? Number(raw) : raw }) as LdapConfig);
  }, []);
  const toggleShow = useCallback(() => setSso((s) => ({ ...s, enabled: !s.enabled })), []);
  const toggleSsl = useCallback(() => setLdap((s) => ({ ...s, enableSsl: !s.enableSsl })), []);
  const selectAll = useCallback((e: FocusEvent<HTMLInputElement>) => e.currentTarget.select(), []);
  const stop = useCallback((e: MouseEvent<HTMLDivElement>) => e.stopPropagation(), []);

  const save = useCallback(() => {
    setBusy(true);
    setErr(null);
    const req =
      state.kind === 'sso'
        ? api.adminSaveProvider(sso).then(() => 'Provider saved.')
        : api.adminSaveLdap(ldap).then(() => 'Connection saved.');
    req
      .then((m) => onSaved(m))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Save failed'))
      .finally(() => setBusy(false));
  }, [state.kind, sso, ldap, onSaved]);

  const title =
    state.kind === 'sso'
      ? sso.name
        ? `Edit ${ssoLabel(sso.type)}`
        : `Add ${preset.label}`
      : ldap.id
        ? 'Edit directory'
        : 'Add LDAP / Active Directory';

  return (
    <div className="bz-cmodal-overlay" onClick={onClose}>
      <div className="bz-cmodal" role="dialog" aria-modal="true" onClick={stop}>
        <div className="bz-cmodal-head">
          <span>{title}</span>
          <button className="bz-cmodal-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="bz-cmodal-body">
          {err && <p className="bz-cnote">{err}</p>}
          {state.kind === 'sso' ? (
            <>
              <div className="bz-cfield">
                <label>Display name (on the button)</label>
                <input className="bz-cinput" data-field="displayName" value={sso.displayName} onChange={onSsoField} placeholder={preset.label} />
              </div>
              <div className="bz-cfield">
                <label>Redirect / callback URL — paste this into your provider</label>
                <input className="bz-cinput" value={callbackUrl} readOnly onFocus={selectAll} />
              </div>
              <div className="bz-cauth-2col">
                <div className="bz-cfield">
                  <label>Client ID</label>
                  <input className="bz-cinput" data-field="clientId" value={sso.clientId} onChange={onSsoField} />
                </div>
                <div className="bz-cfield">
                  <label>Client secret{sso.name ? ' (blank = keep)' : ''}</label>
                  <input className="bz-cinput" type="password" data-field="clientSecret" value={sso.clientSecret ?? ''} onChange={onSsoField} autoComplete="new-password" />
                </div>
              </div>
              {preset.extra === 'domain' && (
                <div className="bz-cfield">
                  <label>{preset.domainLabel}</label>
                  <input className="bz-cinput" data-field="domain" value={sso.domain ?? ''} onChange={onSsoField} />
                </div>
              )}
              {preset.extra === 'issuer' && (
                <div className="bz-cfield">
                  <label>Issuer URL</label>
                  <input
                    className="bz-cinput"
                    data-field="issuerUrl"
                    value={sso.issuerUrl ?? ''}
                    onChange={onSsoField}
                    placeholder="https://idp.example.com"
                  />
                  <p className="bz-cfield-hint">
                    Your provider’s OIDC issuer; the authorization, token and user-info endpoints are
                    discovered automatically from <code>/.well-known/openid-configuration</code>.
                  </p>
                </div>
              )}
              <div className="bz-cfield-row">
                <div>
                  <div className="bz-cfield-title">Show on login page</div>
                  <div className="bz-cfield-hint">Display the “Sign in with…” button.</div>
                </div>
                <Toggle on={sso.enabled} onToggle={toggleShow} label="Show" />
              </div>
            </>
          ) : (
            <>
              <div className="bz-cfield">
                <label>Display name</label>
                <input className="bz-cinput" data-field="serverName" value={ldap.serverName} onChange={onLdapField} placeholder="Company Active Directory" />
              </div>
              <div className="bz-cauth-2col">
                <div className="bz-cfield">
                  <label>Host</label>
                  <input className="bz-cinput" data-field="host" value={ldap.host} onChange={onLdapField} placeholder="ldap.corp.example.com" />
                </div>
                <div className="bz-cfield">
                  <label>Port</label>
                  <input className="bz-cinput bz-cinput-num" type="number" data-field="port" value={ldap.port} onChange={onLdapField} />
                </div>
              </div>
              <div className="bz-cfield-row">
                <div>
                  <div className="bz-cfield-title">Use SSL/TLS (LDAPS)</div>
                  <div className="bz-cfield-hint">Encrypt the connection to the directory (recommended).</div>
                </div>
                <Toggle on={ldap.enableSsl} onToggle={toggleSsl} label="Use SSL" />
              </div>
              <div className="bz-cfield">
                <label>Bind DN</label>
                <input className="bz-cinput" data-field="username" value={ldap.username} onChange={onLdapField} placeholder="cn=admin,dc=corp,dc=example,dc=com" />
              </div>
              <div className="bz-cfield">
                <label>Bind password{ldap.id ? ' (blank = keep)' : ''}</label>
                <input className="bz-cinput" type="password" data-field="password" value={ldap.password ?? ''} onChange={onLdapField} autoComplete="new-password" />
              </div>
              <div className="bz-cfield">
                <label>Base DN</label>
                <input className="bz-cinput" data-field="baseDn" value={ldap.baseDn} onChange={onLdapField} placeholder="ou=users,dc=corp,dc=example,dc=com" />
              </div>
              <div className="bz-cfield">
                <label>User filter</label>
                <input className="bz-cinput" data-field="filter" value={ldap.filter} onChange={onLdapField} placeholder="(objectClass=inetOrgPerson)" />
                <p className="bz-cfield-hint">Optional LDAP filter selecting which entries are users.</p>
              </div>
            </>
          )}
        </div>
        <div className="bz-cmodal-actions">
          <button className="bz-cbtn bz-cbtn-outline" onClick={onClose}>
            Cancel
          </button>
          <button className="bz-cbtn" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── System (Phase B-infra: SFU quality from Prometheus) ─────────────────────

/** Live meetings refresh cadence — fast enough to feel live, slow enough not to
 * hammer the API from an idle dashboard left open. */
const LIVE_POLL_MS = 10_000;

function LiveSection() {
  const [rows, setRows] = useState<LiveMeeting[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let stop = false;
    const load = () => {
      api
        .adminLive()
        .then((r) => {
          if (!stop) {
            setRows(r);
            setLoaded(true);
          }
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, LIVE_POLL_MS);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, []);

  const end = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const slug = e.currentTarget.dataset.slug!;
    if (!window.confirm(`End the meeting "${slug}" for everyone?`)) return;
    void api.adminEndMeeting(slug).then(() => {
      setRows((r) => r.filter((m) => m.slug !== slug));
    });
  }, []);

  return (
    <section>
      <p className="bz-cnote">
        In-progress meetings, refreshed every {LIVE_POLL_MS / 1000}s. Ending a meeting disconnects
        everyone immediately — for emergencies.
      </p>
      <div className="bz-ctable-wrap">
        <table className="bz-ctable">
          <thead>
            <tr>
              <th>Meeting</th>
              <th>Host</th>
              <th>Attendees</th>
              <th>Running for</th>
              <th>E2EE</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.slug}>
                <td>
                  <strong>{m.title ?? m.slug}</strong> <code>{m.slug}</code>
                </td>
                <td>{m.hostName}</td>
                <td>{m.attendeeCount}</td>
                <td>{runningFor(m.startedAt)}</td>
                <td>
                  <span className={m.e2eeModel === 'none' ? 'bz-cpill bz-cpill-gray' : 'bz-cpill bz-cpill-ok'}>
                    {m.e2eeModel === 'b' ? 'zero-knowledge' : m.e2eeModel === 'a' ? 'E2EE' : 'off'}
                  </span>
                </td>
                <td>
                  <button type="button" className="bz-cbtn bz-cbtn-sm bz-cbtn-danger" data-slug={m.slug} onClick={end}>
                    End meeting
                  </button>
                </td>
              </tr>
            ))}
            {loaded && rows.length === 0 && (
              <tr>
                <td className="bz-ctable-empty" colSpan={6}>
                  No meetings in progress.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function runningFor(startedAt: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(startedAt).getTime()) / 60000));
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}

// ── History ─────────────────────────────────────────────────────────────────

function HistorySection() {
  const [rows, setRows] = useState<AdminMeetingHistoryEntry[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .adminHistory()
      .then((r) => {
        setRows(r);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  return (
    <section>
      <div className="bz-ctable-wrap">
        <table className="bz-ctable">
          <thead>
            <tr>
              <th>Meeting</th>
              <th>Host</th>
              <th>Started</th>
              <th>Duration</th>
              <th>Attendees</th>
              <th>E2EE</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.slug}>
                <td>
                  <strong>{m.title ?? m.slug}</strong> <code>{m.slug}</code>
                </td>
                <td>{m.hostName}</td>
                <td>{new Date(m.createdAt).toLocaleString()}</td>
                <td>{fmtDuration(m.durationSeconds)}</td>
                <td>{m.attendees.length}</td>
                <td>
                  <span className={m.e2eeModel === 'none' ? 'bz-cpill bz-cpill-gray' : 'bz-cpill bz-cpill-ok'}>
                    {m.e2eeModel === 'b' ? 'zero-knowledge' : m.e2eeModel === 'a' ? 'E2EE' : 'off'}
                  </span>
                </td>
              </tr>
            ))}
            {loaded && rows.length === 0 && (
              <tr>
                <td className="bz-ctable-empty" colSpan={6}>
                  No meetings yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function fmtDuration(seconds: number): string {
  if (seconds <= 0) return '—';
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

// ── Audit ───────────────────────────────────────────────────────────────────

function AuditSection() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [meeting, setMeeting] = useState('');
  const [identity, setIdentity] = useState('');
  const [searching, setSearching] = useState(false);

  const runSearch = useCallback(async (m?: string, id?: string) => {
    setSearching(true);
    try {
      setEvents(await api.adminAudit({ meeting: m, identity: id }));
    } catch {
      setEvents([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    api
      .adminAudit({})
      .then(setEvents)
      .catch(() => setEvents([]));
  }, []);

  const onSearch = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      void runSearch(meeting.trim() || undefined, identity.trim() || undefined);
    },
    [meeting, identity, runSearch],
  );
  const onMeetingChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMeeting(e.target.value);
  }, []);
  const onIdentityChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setIdentity(e.target.value);
  }, []);

  return (
    <section>
      <p className="bz-cnote">
        Every token issued and every admission decision. Append-only — records can never be edited
        or deleted.
      </p>
      <form className="bz-csearch" onSubmit={onSearch}>
        <input
          className="bz-cinput"
          placeholder="meeting slug"
          value={meeting}
          onChange={onMeetingChange}
          aria-label="Filter by meeting"
        />
        <input
          className="bz-cinput"
          placeholder="identity"
          value={identity}
          onChange={onIdentityChange}
          aria-label="Filter by identity"
        />
        <button className="bz-cbtn bz-cbtn-outline" type="submit" disabled={searching}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>
      <div className="bz-ctable-wrap">
        <table className="bz-ctable">
          <thead>
            <tr>
              <th>Time</th>
              <th>Kind</th>
              <th>Meeting</th>
              <th>Identity</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => (
              <tr key={i}>
                <td>{new Date(e.timestamp).toLocaleString()}</td>
                <td>
                  <span className={`bz-cpill bz-caudit-${e.kind}`}>{e.kind}</span>
                </td>
                <td>{e.meetingSlug}</td>
                <td>{e.name ? `${e.name} (${e.identity})` : e.identity}</td>
                <td className="bz-caudit-detail">{e.detail}</td>
              </tr>
            ))}
            {events.length === 0 && !searching && (
              <tr>
                <td className="bz-ctable-empty" colSpan={5}>
                  No matching audit events.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
